<template>
	<v-card
		:class="cardClass()"
		:style="cardStyle"
		rounded="xl"
		elevation="8"
		class="action-dialog-outer d-flex flex-column overflow-hidden">
		<v-window v-model="currentTab" :touch="false" class="flex-grow-1 overflow-y-auto overflow-x-hidden h-100">
			<v-window-item v-test="'action-tab-photo'" :value="'photo'" class="h-100">
				<ActionPhoto
					ref="actionPhoto"
					:action-text="actionText"
					:project-id="projectId"
					:project="project"
					:actioning-text="actioningText"
					:allow-join="false"
					:button-icon="buttonIcon"
					:show-header="showHeader"
					:project-checklist-item-id="projectChecklistItemId"
					v-bind="instructionDetourPropsFor('photo')"
					@close-menu="close"></ActionPhoto>
			</v-window-item>
			<v-window-item v-test="'action-tab-upload-photo'" :value="'upload-photo'" class="h-100">
				<ActionUploadPhoto
					ref="actionUploadPhoto"
					:action-text="actionText"
					:project-id="projectId"
					:project="project"
					:actioning-text="actioningText"
					:allow-join="false"
					:button-icon="buttonIcon"
					:show-header="showHeader"
					:allow-docs="allowDocs"
					:project-checklist-item-id="projectChecklistItemId"
					@close-menu="close"
					@uploadCounts="(counts) => $emit('uploadCounts', counts)"></ActionUploadPhoto>
			</v-window-item>
			<v-window-item v-test="'action-tab-live-call'" :value="'live-call'">
				<ActionVideo
					ref="actionVideoSync"
					:conversation-type="'sync'"
					:action-text="actionText"
					:project="project"
					:actioning-text="actioningText"
					:allow-join="true"
					:project-id="projectId"
					:button-icon="buttonIcon"
					:show-header="showHeader"
					@close-menu="close"></ActionVideo>
			</v-window-item>
			<v-window-item v-test="'action-tab-schedule'" :value="'schedule'">
				<ActionSchedule
					ref="actionSchedule"
					:project-id="projectId"
					:project="project"
					:show-header="showHeader"
					@close-menu="close"></ActionSchedule>
			</v-window-item>
			<v-window-item v-test="'action-tab-async-recording'" :value="'async-recording'">
				<ActionStartRecording
					ref="actionRecording"
					:project-id="projectId"
					:project="project"
					:project-checklist-item-id="projectChecklistItemId"
					v-bind="instructionDetourPropsFor('async-recording')"
					@close-menu="close"></ActionStartRecording>
			</v-window-item>
			<v-window-item v-test="'action-tab-request-recording'" :value="'request-recording'">
				<ActionVideo
					ref="actionVideoAsync"
					:conversation-type="'async'"
					:action-text="actionText"
					:project="project"
					:actioning-text="actioningText"
					:project-id="projectId"
					:allow-join="false"
					:button-icon="buttonIcon"
					:show-header="showHeader"
					v-bind="instructionDetourPropsFor('request-recording')"
					@close-menu="close"></ActionVideo>
			</v-window-item>
		</v-window>
	</v-card>
</template>

<script>
	import "../helpers/emoji";
	import ActionVideo from "./ActionVideo.vue";
	import ActionPhoto from "./ActionPhoto.vue";
	import ActionUploadPhoto from "./ActionUploadPhoto.vue";
	import ActionSchedule from "./ActionSchedule.vue";
	import ActionStartRecording from "./ActionStartRecording.vue";
	import DeviceUtility from "@/helpers/device";
	import { getVueTelProps } from "@/helpers/phone";
	import api from "../api";

	export default {
		components: {
			ActionVideo,
			ActionSchedule,
			ActionPhoto,
			ActionStartRecording,
			ActionUploadPhoto
		},
		props: {
			conversationType: String, //sync or async
			customerId: String,
			actionText: String,
			actioningText: String,
			allowJoin: Boolean,
			buttonIcon: String,
			showHeader: Boolean,
			tabAction: String,
			projectId: String,
			project: {
				type: Object,
				required: false
			},
			allowDocs: {
				type: Boolean,
				default: true
			},
			projectChecklistItemId: {
				type: String,
				default: ""
			},
			// LSP-16289: threaded through to the Start Recording tab so it can reopen preselected
			// with the instruction created during the in-app editor detour.
			initialInstructionId: {
				type: String,
				default: null
			},
			instructionCreateReturnTo: {
				type: String,
				default: null
			},
			instructionCreateDraftKey: {
				type: String,
				default: null
			}
		},
		emits: ["close-menu", "uploadCounts"],
		created() {
			this.currentTab = this.tabAction;
		},
		data() {
			return {
				currentTab: null,
				vueTelProps: getVueTelProps(),
				starting: false,
				validPhone: false,
				autoSetName: false,
				autoSetNameValue: "",
				phone: "",
				name: "",
				includeEmailInvitation: "",
				meetingUrl: "",
				actioningTextOverride: "",
				organization: {},
				autoStartRecordings: true,
				notificationTemplate: "",
				defaultNotificationTemplate: "",
				me: null
			};
		},
		computed: {
			tabClass() {
				return `tabs-${this.currentTab}`;
			},
			dynamicActioningText() {
				if (this.actioningTextOverride) {
					return this.actioningTextOverride;
				}
				return this.starting ? this.actioningText : this.actionText;
			},
			dynamicName() {
				return this.nameOverride ?? this.name;
			},
			cardStyle() {
				// Force 425px width on tablets (not mobile, not desktop)
				const isTablet = DeviceUtility.isTablet && !DeviceUtility.isMobile;
				return isTablet ? { maxWidth: "425px", width: "425px" } : {};
			}
		},
		watch: {
			currentTab: {
				handler(newValue) {
					this.reset();
				}
			},
			tabAction: {
				handler(newValue) {
					this.currentTab = newValue;
				}
			}
		},
		async mounted() {
			this.me = await api.getMe();
			this.organization = this.me.$organization;
		},
		methods: {
			// LSP-16289: the create-and-return detour context belongs only to the tab matching the
			// returned action. v-window mounts only the active tab today, but gating the forwarding
			// makes the "only the originating dialog reopens preselected" invariant explicit — and
			// robust if an inactive tab ever mounts (an `eager` window, a future tab switcher, or a
			// Vuetify change), where it would otherwise consume and clear the shared sessionStorage
			// draft before the active tab restores.
			instructionDetourPropsFor(tabValue) {
				if (this.currentTab !== tabValue) {
					return { initialInstructionId: null, instructionCreateReturnTo: null, instructionCreateDraftKey: null };
				}
				return {
					initialInstructionId: this.initialInstructionId,
					instructionCreateReturnTo: this.instructionCreateReturnTo,
					instructionCreateDraftKey: this.instructionCreateDraftKey
				};
			},
			cardClass() {
				return `pa-1 conversation-card ${DeviceUtility.isMobile ? "conversation-bottom" : ""} ${this.conversationType}`;
			},
			close(conversationId, isBack = false) {
				console.log("ActionDialog close " + conversationId);
				this.$emit("close-menu", conversationId, isBack);
			},
			reset() {
				this.$refs.actionVideoSync?.reset();
				this.$refs.actionSchedule?.reset();
				this.$refs.actionRecording?.reset();
				this.$refs.actionVideoAsync?.reset();
				this.$refs.actionPhoto?.reset();
				this.$refs.actionUploadPhoto?.reset();
			}
		}
	};
</script>

<style lang="scss" scoped>
	.start-conversation-button {
		font-family: "Inter Tight", sans-serif;
		font-size: 20px;
		color: white;
		margin-left: 10px;
	}
	.reset-span {
		position: relative;
		left: calc(100% - 50px);
		display: inline-block;
		top: -30px;
		height: 0;
	}

	.conversation-card {
		border-radius: 12px;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
	}

	@media screen and (max-width: 500px) {
		.conversation-card.action-dialog-outer {
			height: 85vh;
			height: 85svh;
			max-height: 85vh;
			max-height: 85svh;
			z-index: 101 !important;
		}
	}

	.conversation-card .header {
		display: flex;
		margin-bottom: 28px;
		margin-top: 12px;
	}

	.conversation-card textarea {
		color: #212121;
		font-size: 16px;
	}

	.conversation-card h2,
	.conversation-card i {
		font-size: 24px;
		margin-right: 5px;
	}

	.conversation-card .header {
		color: #000;
	}

	.conversation-card .auto-message {
		color: #b3b3b3;
		font-size: 16px;
	}

	.conversation-card .record-switch {
		margin-left: 5px;
		margin-top: 0px;
	}

	.conversation-card .contact-name.reset {
		padding-top: 0px;
	}

	.field-border-radius {
		border-radius: 6px;
	}

	.v-slide-group__next,
	.v-slide-group__prev {
		display: none;
	}

	/* Action Highlights */

	.v-tab i {
		color: #000 !important;
	}

	.tab-active-live-call {
		background-color: #ffe6cc !important;
	}

	.tab-active-photo i {
		color: #ff8a00 !important;
	}

	.tab-active-photo {
		background-color: #ffe6cc !important;
	}

	.tab-active-upload-photo i {
		color: #ff8a00 !important;
	}

	.tab-active-upload-photo {
		background-color: #ffe6cc !important;
	}

	.tab-active-live-call i {
		color: #ff8a00 !important;
	}

	.tab-active-schedule {
		background-color: #bcf7ed !important;
	}

	.tab-active-schedule i {
		color: #009785 !important;
	}

	.tab-active-record {
		background-color: #ffcccc !important;
	}

	.tab-active-record i {
		color: #ff460b !important;
	}

	.tab-active-request {
		background-color: #d8ddff !important;
	}

	.tab-active-request i {
		color: #3d5afe !important;
	}

	.tabs-live-call {
		background-color: #fff8f1;
	}

	.tabs-photo {
		background-color: #fff8f1;
	}

	.tabs-upload-photo {
		background-color: #fff8f1;
	}

	.tabs-schedule {
		background-color: #f1fffc;
	}

	.tabs-async-recording {
		background-color: #fff0f2;
	}

	.tabs-request-recording {
		background-color: #f2f0ff;
	}
</style>
<style>
	/* Tablet-specific dialog sizing and positioning */
	@media screen and (min-width: 600px) and (max-width: 1024px) {
		/* Force dialog content wrapper to 425px width */
		.vue-tel-input-menu.new-conversation {
			max-width: 425px !important;
			width: 425px !important;
			margin: 0 !important;
		}

		/* Ensure all conversation cards are constrained to 425px on tablet */
		.vue-tel-input-menu.new-conversation .conversation-card {
			max-width: 425px !important;
			width: 425px !important;
		}
	}

	/* Landscape mobile/tablet: single scroll container fix.
	   Suppress scrolling on the v-window and inner card so that only
	   v-card-text scrolls, with v-card-actions pinned at the bottom. */
	@media screen and (min-width: 600px) and (max-width: 1024px) and (orientation: landscape), screen and (max-height: 500px) {
		/* Outer card: constrain to viewport and provide definite height so flex children can resolve their 100% height.
		   flex override is required because Vuetify's scrollable dialog sets flex: 1 1 100% on the card,
		   and flex-basis takes precedence over height in flex layout — so height alone is ignored. */
		.conversation-card.action-dialog-outer {
			flex: 0 0 auto !important;
			height: calc(100vh - 48px) !important;
			height: calc(100dvh - 48px) !important;
			max-height: calc(100vh - 48px) !important;
			max-height: calc(100dvh - 48px) !important;
			min-height: auto !important;
			overflow: hidden !important;
		}

		/* v-window: fill parent, stop being a scroll container */
		.action-dialog-outer .v-window {
			overflow: hidden !important;
		}

		/* v-window internals: propagate height down to inner cards */
		.action-dialog-outer .v-window__container,
		.action-dialog-outer .v-window-item {
			height: 100% !important;
		}

		/* Inner action cards: fill v-window-item, flex column, no scroll */
		.action-dialog-outer .v-window-item > .v-card {
			display: flex !important;
			flex-direction: column !important;
			height: 100% !important;
			max-height: 100% !important;
			min-height: auto !important;
			overflow: hidden !important;
		}

		/* v-card-text: the single scroll container */
		.action-dialog-outer .v-window-item > .v-card > .v-card-text {
			flex: 1 1 0 !important;
			overflow-y: auto !important;
			min-height: 0 !important;
			-webkit-overflow-scrolling: touch;
		}

		/* v-card-actions: pinned at bottom */
		.action-dialog-outer .v-window-item > .v-card > .v-card-actions {
			flex-shrink: 0 !important;
		}
	}

	/* Mobile Landscape Override: Allow wider width for mobile landscape */
	@media screen and (min-width: 600px) and (max-width: 1024px) and (orientation: landscape) {
		/* Target the dialog content wrapper when it has .conversation-bottom (mobile) */
		.vue-tel-input-menu.new-conversation:has(.conversation-bottom) {
			max-width: 95% !important;
			width: 95% !important;
		}

		.vue-tel-input-menu.new-conversation .conversation-card.conversation-bottom {
			max-width: 100% !important;
			width: 100% !important;
		}
	}
	.conversation-bottom {
		width: 100% !important;
		border-radius: 0 !important;
	}
</style>
